import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BedDouble, Users, CalendarCheck } from "lucide-react";

export default function Rooms() {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [ci, setCi] = useState(today);
  const [co, setCo] = useState(tomorrow);
  const [rooms, setRooms] = useState([]);
  const [checked, setChecked] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ guests: 1, customer_name: "", customer_phone: "", customer_email: "", notes: "" });
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr(""); setMsg(null);
    try {
      const { data } = await api.get("/rooms/availability", { params: { check_in: ci, check_out: co } });
      setRooms(data); setChecked(true);
    } catch (e) { setErr(e.response?.data?.detail || "Could not check availability"); }
  };

  useEffect(() => { api.get("/rooms").then((r) => setRooms(r.data)); }, []);

  const book = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(null);
    try {
      const { data } = await api.post("/bookings", { ...form, room_id: selected.room_id, check_in: ci, check_out: co });
      setMsg(`Booking request received! Your reference: ${data.booking_number}. Our team will confirm shortly.`);
      setSelected(null);
      setForm({ guests: 1, customer_name: "", customer_phone: "", customer_email: "", notes: "" });
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Booking failed"); }
  };

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6" data-testid="rooms-page">
      <span className="badge-gold">Stay</span>
      <h1 className="font-serif text-4xl sm:text-5xl text-white mt-2">Room Availability</h1>
      <p className="text-zinc-400 mt-2 max-w-2xl">Pick your dates — our team confirms the room request within hours.</p>

      <div className="card-lux p-5 mt-6 grid sm:grid-cols-4 gap-3 items-end">
        <label className="block">
          <div className="text-xs text-zinc-400 mb-1">Check-in</div>
          <input data-testid="room-ci" type="date" min={today} value={ci} onChange={(e) => setCi(e.target.value)} className="input-lux" />
        </label>
        <label className="block">
          <div className="text-xs text-zinc-400 mb-1">Check-out</div>
          <input data-testid="room-co" type="date" min={ci} value={co} onChange={(e) => setCo(e.target.value)} className="input-lux" />
        </label>
        <div className="sm:col-span-2 flex justify-end">
          <button data-testid="check-availability-btn" onClick={load} className="btn-gold">
            <CalendarCheck size={16} /> Check availability
          </button>
        </div>
      </div>

      {err && <div className="text-red-400 text-sm mt-3">{err}</div>}
      {msg && <div className="card-lux p-4 mt-4 border-amber-500/40" data-testid="booking-success">{msg}</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {rooms.map((r) => (
          <div key={r.room_id} className="card-lux overflow-hidden flex flex-col" data-testid={`room-${r.room_id}`}>
            {r.image_url && <img src={r.image_url} alt={r.name} className="w-full aspect-[5/4] object-cover" />}
            <div className="p-4 flex-1 flex flex-col">
              <h3 className="font-serif text-xl text-white">{r.name}</h3>
              <p className="text-sm text-zinc-400 mt-1">{r.description}</p>
              <div className="mt-3 flex items-center gap-3 text-xs text-zinc-400">
                <span className="flex items-center gap-1"><Users size={12} /> {r.capacity} guests</span>
                <span className="flex items-center gap-1"><BedDouble size={12} /> {r.total_units} total</span>
              </div>
              {r.amenities?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {r.amenities.slice(0, 4).map((a) => (
                    <span key={a} className="text-[10px] px-2 py-0.5 rounded-full border border-zinc-700 text-zinc-400">{a}</span>
                  ))}
                </div>
              )}
              <div className="mt-auto pt-4">
                {checked ? (
                  r.is_available ? (
                    <button
                      data-testid={`book-${r.room_id}`}
                      className="btn-gold w-full"
                      onClick={() => setSelected(r)}
                    >Request booking ({r.available_units} available)</button>
                  ) : (
                    <button disabled className="btn-outline-gold w-full opacity-60">Fully booked for selected dates</button>
                  )
                ) : (
                  <button className="btn-outline-gold w-full" onClick={load}>Check availability</button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="booking-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setSelected(null)} />
          <form onSubmit={book} className="relative card-lux w-full max-w-md p-5 space-y-3">
            <h3 className="font-serif text-2xl text-amber-200">Book {selected.name}</h3>
            <div className="text-xs text-zinc-400 font-mono">{ci} → {co}</div>
            <input required placeholder="Full name" className="input-lux" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="bk-name" />
            <input required placeholder="Phone" className="input-lux" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="bk-phone" />
            <input placeholder="Email (optional)" className="input-lux" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="bk-email" />
            <input type="number" min={1} max={selected.capacity} className="input-lux" value={form.guests} onChange={(e) => setForm({ ...form, guests: +e.target.value })} data-testid="bk-guests" />
            <textarea placeholder="Notes (optional)" className="input-lux" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <div className="flex gap-2">
              <button type="button" onClick={() => setSelected(null)} className="btn-outline-gold flex-1">Cancel</button>
              <button type="submit" data-testid="confirm-booking-btn" className="btn-gold flex-1">Send request</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
